import { tickKey } from '../../../util.js';
import type { GenerateScaleProps, Scale } from '../scale.types';

export const generatePointScale = ({ domain, range, padding }: GenerateScaleProps = {}): Scale<string> | null => {
  if (!Array.isArray(range) || range.length !== 2 || !range.every(Number.isFinite)) return null;
  if (!Array.isArray(domain) || !domain.length) return null;

  const keys: string[] = domain.map((entry) => tickKey(entry));
  const indexes = new Map(keys.map((key, index) => [key, index]));
  const reversed = range[1] < range[0];
  const start = Math.min(range[0], range[1]);
  const span = Math.abs(range[1] - range[0]);
  const requested = Number(padding ?? 0.5);
  const point_padding = Number.isFinite(requested) ? Math.max(0, requested) : 0.5;
  const step = keys.length < 2 || !(span > 0) ? 0 : span / (keys.length - 1 + 2 * point_padding);

  const scale = ((value: unknown) => {
    const at = indexes.get(tickKey(value));
    if (at === undefined) return undefined;
    if (keys.length === 1) return start + span / 2;
    const index = reversed ? keys.length - 1 - at : at;
    return start + step * (index + point_padding);
  }) as Scale<string>;

  scale.domain = () => [...keys];
  scale.range = () => [range[0], range[1]];
  scale.ticks = () => [...keys];
  scale.invert = (position: number) => {
    if (!Number.isFinite(position)) return undefined;
    if (keys.length === 1) return keys[0];
    if (!(step > 0)) return undefined;
    const slot = Math.max(0, Math.min(keys.length - 1, Math.round((position - start) / step - point_padding)));
    return keys[reversed ? keys.length - 1 - slot : slot];
  };
  scale.bandwidth = () => 0;
  scale.step = () => step;
  scale.padding = () => point_padding;

  return scale;
};
